import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Leaf, Shield, Target, BarChart3, Globe, Heart } from "lucide-react";
import { SocialShare } from "@/components/SocialShare";

const IntegrityRange = () => {
  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-primary-foreground text-primary">ESG Range</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Integrity Range Portfolios</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-4">
              ESG-Focused Investment Solutions
            </p>
            <div className="flex justify-center items-center gap-8 text-lg mb-6">
              <div>
                <span className="font-semibold">Status:</span> Research & Development
              </div>
              <div>
                <span className="font-semibold">Focus:</span> Sustainable Investing
              </div>
            </div>
            <div className="flex justify-center">
              <SocialShare title="CBAM Integrity Range Portfolios" variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Portfolio Overview</h2>
              <p className="text-lg text-muted-foreground mb-6">
                The Integrity Range represents our commitment to Environmental, Social, and Governance (ESG) focused
                investment solutions, currently in the research and development phase.
              </p>
              <p className="text-muted-foreground mb-6">
                These portfolios will focus on sustainable investing principles, integrating ESG factors into our
                investment decision-making process while maintaining our rigorous approach to portfolio management.
              </p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-5 w-5" />
                  ESG Focus Areas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Environmental sustainability
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Social responsibility
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Corporate governance
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Impact measurement
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ESG Approach */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Our ESG Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Environmental</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Focus on companies with strong environmental practices, climate change mitigation, and sustainable
                  resource management.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Social</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Investment in companies that demonstrate strong social responsibility, employee welfare, and positive
                  community impact.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Governance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Emphasis on companies with excellent corporate governance, transparency, and ethical business practices.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Performance Analytics - Development Phase */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Performance Framework</h2>
            <p className="text-xl text-muted-foreground">
              ESG performance metrics and impact measurement framework in development
            </p>
          </div>
          
          {/* Performance Graph Placeholder */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>ESG Performance & Impact Chart</CardTitle>
              <CardDescription>Future: Performance tracking with ESG impact metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-80 bg-muted/30 rounded-lg flex items-center justify-center border-2 border-dashed border-muted">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">ESG Performance Chart - In Development</p>
                  <p className="text-sm text-muted-foreground">Will show performance vs benchmark + ESG impact metrics</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ESG Metrics Framework */}
          <Card>
            <CardHeader>
              <CardTitle>Planned ESG Metrics Framework</CardTitle>
              <CardDescription>Performance and impact measurement structure under development</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 border border-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Financial Performance</h4>
                  <p className="text-sm text-muted-foreground">Traditional return metrics vs ESG benchmarks</p>
                </div>
                <div className="text-center p-4 border border-muted rounded-lg">
                  <h4 className="font-semibold mb-2">ESG Scores</h4>
                  <p className="text-sm text-muted-foreground">Portfolio-level environmental, social & governance ratings</p>
                </div>
                <div className="text-center p-4 border border-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Impact Metrics</h4>
                  <p className="text-sm text-muted-foreground">Measurable positive impact outcomes</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                Comprehensive performance and ESG impact tracking will be available upon portfolio launch.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Research & Development */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Research & Development Phase</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We are currently developing our ESG framework and portfolio construction methodology
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">Development Timeline</CardTitle>
                <CardDescription className="text-center">
                  Our approach to launching the Integrity Range with robust ESG integration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold">ESG Framework Development</h4>
                      <p className="text-muted-foreground">
                        Establishing comprehensive ESG criteria and screening methodologies
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold">Investment Universe Analysis</h4>
                      <p className="text-muted-foreground">
                        Researching ESG-compliant investment options across asset classes
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold">Impact Measurement</h4>
                      <p className="text-muted-foreground">
                        Developing metrics to measure and report on ESG impact and performance
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-3 h-3 bg-muted rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-muted-foreground">Portfolio Launch</h4>
                      <p className="text-muted-foreground">
                        Full launch following comprehensive testing and regulatory approval
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stay Updated */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-xl text-muted-foreground">
              Be the first to know when the Integrity Range becomes available
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Register Your Interest</CardTitle>
                <CardDescription>
                  Get notified when the Integrity Range portfolios are ready for investment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full">
                  Register Interest
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>ESG Resources</CardTitle>
                <CardDescription>
                  Learn more about our approach to sustainable investing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" variant="outline">
                  Download ESG Guide
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Interested in Sustainable Investing?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Contact our team to discuss ESG investment options and be notified when the Integrity Range launches.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">Contact Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default IntegrityRange;